import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AuthGuard } from '@/shared/lib/auth/auth-guard'
import { AppLayout } from '@/shared/components/layout/app-layout'
import { DemoPage } from '@/shared/components/demo-page'
import { BankAccountsPage } from '@/features/bank-accounts/components/bank-accounts-page'
import { PermissionRoute } from '@/shared/lib/permissions/permission-route'
import { ReconTenantView } from '@/features/reconciliation/components/recon-tenant-view'
import { Permissions, PermissionLevel } from '@/shared/lib/permissions/permission-values'
import { isWorkflowStudioEnabled } from '@/features/reconciliation/workflow-studio/studio-availability'
import { LegacyIngestionRedirect } from '@/features/ingestion-activity/components/legacy-ingestion-redirect'
import { INGESTION_PATHS } from '@/features/ingestion-activity/routes'
import { ReportsRoute } from '@/features/datalake-dashboards/components/reports/reports-route'

const RoadmapPage = lazy(() =>
  import('@/features/roadmap/components/roadmap-page').then((m) => ({ default: m.RoadmapPage })),
)
const StatusBoardPage = lazy(() =>
  import('@/features/status-board/components/status-board-page').then((module) => ({
    default: module.StatusBoardPage,
  })),
)
const DataExportsPage = lazy(() =>
  import('@/features/data/components/data-exports-page').then((m) => ({ default: m.DataExportsPage })),
)
const DataHubPage = lazy(() =>
  import('@/features/data/components/data-hub-page').then((m) => ({ default: m.DataHubPage })),
)
const FxContractsPage = lazy(() =>
  import('@/features/data/components/fx-contracts-page').then((m) => ({ default: m.FxContractsPage })),
)
const DebtsContractsPage = lazy(() =>
  import('@/features/data/components/debts-contracts-page').then((m) => ({ default: m.DebtsContractsPage })),
)
const InvestmentsContractsPage = lazy(() =>
  import('@/features/data/components/investments-contracts-page').then((m) => ({ default: m.InvestmentsContractsPage })),
)
const DataRepositoryPage = lazy(() =>
  import('@/features/data/components/data-repository-page').then((m) => ({ default: m.DataRepositoryPage })),
)
const FinancialEventsPage = lazy(() =>
  import('@/features/data/financial-events/financial-events-page').then((m) => ({ default: m.FinancialEventsPage })),
)

const CustomEntityBuilderPage = lazy(() =>
  import('@/features/data/components/custom-entity-builder-page').then((m) => ({ default: m.CustomEntityBuilderPage })),
)
const FxBlotterPage = lazy(() =>
  import('@/features/reporting/components/fx-blotter-page').then((m) => ({ default: m.FxBlotterPage })),
)
const LoansBlotterPage = lazy(() =>
  import('@/features/reporting/components/loans-blotter-page').then((m) => ({ default: m.LoansBlotterPage })),
)
const InvestmentsBlotterPage = lazy(() =>
  import('@/features/reporting/components/investments-blotter-page').then((m) => ({ default: m.InvestmentsBlotterPage })),
)
const InvestmentsMovementsPage = lazy(() =>
  import('@/features/reporting/components/infra-investments/investments-movements-page').then((m) => ({ default: m.InvestmentsMovementsPage })),
)
const DebtRepaymentPage = lazy(() =>
  import('@/features/reporting/components/debt-repayment-page').then((m) => ({ default: m.DebtRepaymentPage })),
)
const CustomerAgingPage = lazy(() =>
  import('@/features/reporting/components/customer-aging-page').then((m) => ({ default: m.CustomerAgingPage })),
)
const AlertsIntegrationsPage = lazy(() =>
  import('@/features/alerts/components/alerts-integrations-page').then((m) => ({ default: m.AlertsIntegrationsPage })),
)
const AlertsDataPage = lazy(() =>
  import('@/features/alerts/components/alerts-data-page').then((m) => ({ default: m.AlertsDataPage })),
)
const AlertsPaymentsPage = lazy(() =>
  import('@/features/alerts/components/alerts-payments-page').then((m) => ({ default: m.AlertsPaymentsPage })),
)
const GlPostingAlertsPage = lazy(() =>
  import('@/features/alerts/components/gl-posting-alerts-page').then((m) => ({ default: m.GlPostingAlertsPage })),
)
const AlertsReportingPage = lazy(() =>
  import('@/features/alerts/components/alerts-reporting-page').then((m) => ({ default: m.AlertsReportingPage })),
)
const AlertsWorkflowsPage = lazy(() =>
  import('@/features/alerts/components/alerts-workflows-page').then((m) => ({ default: m.AlertsWorkflowsPage })),
)
const StaticDataPage = lazy(() =>
  import('@/features/admin/components/static-data-page').then((m) => ({ default: m.StaticDataPage })),
)
const AdminConsolePage = lazy(() =>
  import('@/features/admin/components/admin-console-page').then((m) => ({ default: m.AdminConsolePage })),
)

const AuthCallback = lazy(() =>
  import('@/features/auth/components/auth-callback').then((module) => ({ default: module.AuthCallback })),
)
const InvitationOutcomePage = lazy(() =>
  import('@/features/auth/components/invitation-outcome-page').then((module) => ({
    default: module.InvitationOutcomePage,
  })),
)
const ClassificationRulesPage = lazy(() =>
  import('@/features/tagging/components/rules/rules-page').then((m) => ({ default: m.RulesPage })),
)
const ClassificationConflictsPage = lazy(() =>
  import('@/features/tagging/components/conflicts/conflicts-page').then((m) => ({ default: m.ConflictsPage })),
)
const ClassificationTagsPage = lazy(() =>
  import('@/features/tagging/components/tags/tags-page').then((m) => ({ default: m.TagsPage })),
)
const ClassificationRuleEditorPage = lazy(() =>
  import('@/features/tagging/components/rules/rule-editor-page').then((m) => ({ default: m.RuleEditorPage })),
)
const CounterpartiesPage = lazy(() =>
  import('@/features/counterparties/components/counterparties-page').then((module) => ({
    default: module.CounterpartiesPage,
  })),
)
const CompanyGroupsPage = lazy(() =>
  import('@/features/company-groups/components/company-groups-page').then((m) => ({ default: m.CompanyGroupsPage })),
)
const CompaniesPage = lazy(() =>
  import('@/features/companies/components/companies-page').then((m) => ({ default: m.CompaniesPage })),
)
const RolesPage = lazy(() =>
  import('@/features/roles/components/roles-page').then((module) => ({ default: module.RolesPage })),
)
const AuditTrailPage = lazy(() =>
  import('@/features/audit/components/audit-trail-page').then((module) => ({ default: module.AuditTrailPage })),
)
const GroupsPage = lazy(() =>
  import('@/features/groups/components/groups-page').then((m) => ({ default: m.GroupsPage })),
)
const UsersPage = lazy(() =>
  import('@/features/users/components/users-page').then((m) => ({ default: m.UsersPage })),
)
const InvoicesPage = lazy(() =>
  import('@/features/invoices/components/invoices-page').then((m) => ({ default: m.InvoicesPage })),
)
const TransactionsPage = lazy(() =>
  import('@/features/transactions/components/transactions-page').then((m) => ({ default: m.TransactionsPage })),
)
const PaymentsPage = lazy(() =>
  import('@/features/payments/components/payments-page').then((m) => ({ default: m.PaymentsPage })),
)
const ExchangeRatesPage = lazy(() =>
  import('@/features/exchange-rates/components/exchange-rates-page').then((m) => ({ default: m.ExchangeRatesPage })),
)
const BankStatementsPage = lazy(() =>
  import('@/features/bank-statements/components/bank-statements-page').then((m) => ({ default: m.BankStatementsPage })),
)
const PaymentApprovalRulesPage = lazy(() =>
  import('@/features/payment-approval-rules/components/payment-approval-rules-page')
    .then((module) => ({ default: module.PaymentApprovalRulesPage })),
)
const ApiAccessPage = lazy(() =>
  import('@/features/api-access/components/api-access-page').then((m) => ({ default: m.ApiAccessPage })),
)
const AiUsagePage = lazy(() =>
  import('@/features/ai-usage/components/ai-usage-page').then((loaded) => ({ default: loaded.AiUsagePage })),
)
const AccessTokensPage = lazy(() =>
  import('@/features/access-tokens/components/access-tokens-page').then((m) => ({ default: m.AccessTokensPage })),
)
const ReportsPage = lazy(() =>
  import('@/features/reports/components/reports-page').then((m) => ({ default: m.ReportsPage })),
)
const DashboardPage = lazy(() =>
  import('@/features/dashboard/components/dashboard-page').then((m) => ({ default: m.DashboardPage })),
)
const CashOverviewPage = lazy(() =>
  import('@/features/cash-overview/components/cash-overview-page').then((m) => ({ default: m.CashOverviewPage })),
)
const CategoryPositionsPage = lazy(() =>
  import('@/features/category-positions/components/category-positions-page').then((page) => ({ default: page.CategoryPositionsPage })),
)
const BalanceDifferencePage = lazy(() =>
  import('@/features/balance-difference/components/balance-difference-page').then((page) => ({ default: page.BalanceDifferencePage })),
)
const CashPositionPage = lazy(() =>
  import('@/features/cash-forecast/components/cash-position/cash-position-page').then((m) => ({ default: m.CashPositionPage })),
)
const PaymentDetailPage = lazy(() =>
  import('@/features/payments/components/payment-detail-page').then((m) => ({ default: m.PaymentDetailPage })),
)
const PaymentApprovalPage = lazy(() =>
  import('@/features/payments/components/payment-approval-page').then((m) => ({ default: m.PaymentApprovalPage })),
)
const NetSettlementsPage = lazy(() =>
  import('@/features/payments/components/net-settlements/net-settlements-page').then((m) => ({ default: m.NetSettlementsPage })),
)
const PaymentLifeCycleBlotterPage = lazy(() =>
  import('@/features/payments/components/payment-life-cycle-blotter/payment-life-cycle-blotter-page').then((m) => ({ default: m.PaymentLifeCycleBlotterPage })),
)
const GlPostingLifeCyclePage = lazy(() =>
  import('@/features/reporting/components/gl-posting-life-cycle/gl-posting-life-cycle-page').then((m) => ({ default: m.GlPostingLifeCyclePage })),
)
const PrefundingAlertsPage = lazy(() =>
  import('@/features/alerts/components/prefunding-alerts/prefunding-alerts-page').then((m) => ({ default: m.PrefundingAlertsPage })),
)
const OpenBankingCallback = lazy(() =>
  import('@/features/bank-accounts/components/open-banking-callback').then((m) => ({ default: m.OpenBankingCallback })),
)
const ServicesPage = lazy(() =>
  import('@/features/integrations/components/services-page').then((m) => ({ default: m.ServicesPage })),
)
const ConnectionDetailPage = lazy(() =>
  import('@/features/integrations/components/connection-detail/connection-detail-page').then(
    (module) => ({ default: module.ConnectionDetailPage }),
  ),
)
const IntStatusPage = lazy(() =>
  import('@/features/integrations/components/status-page').then((m) => ({ default: m.StatusPage })),
)
const WebhooksPage = lazy(() =>
  import('@/features/integrations/components/webhooks-page').then((m) => ({ default: m.WebhooksPage })),
)
const EmailIngestionPage = lazy(() =>
  import('@/features/email-ingestion/components/email-ingestion-page').then((module) => ({
    default: module.EmailIngestionPage,
  })),
)
const MailboxMessagesPage = lazy(() =>
  import('@/features/email-ingestion/messages/components/mailbox-messages-page').then((module) => ({
    default: module.MailboxMessagesPage,
  })),
)
const ApiAgentPage = lazy(() =>
  import('@/features/integrations/components/api-agent-page').then((m) => ({ default: m.ApiAgentPage })),
)
const AgentsDashboardPage = lazy(() =>
  import('@/features/agents/components/agents-dashboard-page').then((m) => ({ default: m.AgentsDashboardPage })),
)
const AgentBuilderPage = lazy(() =>
  import('@/features/agents/components/agent-builder-page').then((m) => ({ default: m.AgentBuilderPage })),
)
const AgentApprovalsPage = lazy(() =>
  import('@/features/agents/components/agent-approvals-page').then((m) => ({ default: m.AgentApprovalsPage })),
)
const AgentChatsPage = lazy(() =>
  import('@/features/agents/components/agent-chats-page').then((m) => ({ default: m.AgentChatsPage })),
)
const AgentLogsPage = lazy(() =>
  import('@/features/agents/components/agent-logs-page').then((m) => ({ default: m.AgentLogsPage })),
)
const AlertsDashboardPage = lazy(() =>
  import('@/features/alerts/components/alerts-dashboard-page').then((m) => ({ default: m.AlertsDashboardPage })),
)
const AlertsAgentsPage = lazy(() =>
  import('@/features/alerts/components/alerts-agents-page').then((m) => ({ default: m.AlertsAgentsPage })),
)
const AlertsAdminPage = lazy(() =>
  import('@/features/alerts/components/alerts-admin-page').then((m) => ({ default: m.AlertsAdminPage })),
)
const TransactionsHierarchicalPage = lazy(() =>
  import('@/features/transactions/components/transactions-hierarchical-page').then((m) => ({ default: m.TransactionsHierarchicalPage })),
)
const WorkflowsDashboardPage = lazy(() =>
  import('@/features/workflows/components/workflows-dashboard-page').then((m) => ({ default: m.WorkflowsDashboardPage })),
)
const WorkflowBuilderPage = lazy(() =>
  import('@/features/workflows/components/workflow-builder-page').then((m) => ({ default: m.WorkflowBuilderPage })),
)
const WorkflowApprovalsPage = lazy(() =>
  import('@/features/workflows/components/workflow-approvals-page').then((m) => ({ default: m.WorkflowApprovalsPage })),
)
// Datalake dashboards POC (UC-100)
const DatalakeCashPositionPage = lazy(() =>
  import('@/features/datalake-dashboards/components/cash-position-page').then((module) => ({ default: module.CashPositionPage })),
)
const DatalakeBalanceChangePage = lazy(() =>
  import('@/features/datalake-dashboards/components/balance-change-page').then((module) => ({ default: module.BalanceChangePage })),
)
const DatalakeAccountStatementPage = lazy(() =>
  import('@/features/datalake-dashboards/components/account-statement-page').then((module) => ({ default: module.AccountStatementPage })),
)
const DatalakeCashFlowPage = lazy(() =>
  import('@/features/datalake-dashboards/components/cash-flow-page').then((module) => ({ default: module.CashFlowPage })),
)
const DatalakeReportsListPage = lazy(() =>
  import('@/features/datalake-dashboards/components/reports/reports-list-page').then((module) => ({ default: module.ReportsListPage })),
)
const DatalakeReportEditorPage = lazy(() =>
  import('@/features/datalake-dashboards/components/reports/report-editor-page').then((module) => ({ default: module.ReportEditorPage })),
)
const ReportingDashboardPage = lazy(() =>
  import('@/features/reporting/components/reporting-dashboard-page').then((m) => ({ default: m.ReportingDashboardPage })),
)
const ReportBuilderPage = lazy(() =>
  import('@/features/reporting/components/report-builder-page').then((m) => ({ default: m.ReportBuilderPage })),
)
const ReportingAlertsPage = lazy(() =>
  import('@/features/reporting/components/reporting-alerts-page').then((m) => ({ default: m.ReportingAlertsPage })),
)
const ReportingAuditTrailPage = lazy(() =>
  import('@/features/reporting/components/reporting-audit-trail-page').then((m) => ({ default: m.ReportingAuditTrailPage })),
)
const RiskCockpitPage = lazy(() =>
  import('@/features/risk-management/components/risk-cockpit/risk-cockpit-page').then((m) => ({ default: m.RiskCockpitPage })),
)
const MtmPage = lazy(() =>
  import('@/features/risk-management/components/mtm/mtm-page').then((m) => ({ default: m.MtmPage })),
)
const FxExposurePage = lazy(() =>
  import('@/features/risk-management/components/fx-exposure/fx-exposure-page').then((m) => ({ default: m.FxExposurePage })),
)
const CreditRiskPage = lazy(() =>
  import('@/features/risk-management/components/credit-risk/credit-risk-page').then((m) => ({ default: m.CreditRiskPage })),
)
const InvestmentRiskPage = lazy(() =>
  import('@/features/risk-management/components/investments/investment-risk-page').then((m) => ({ default: m.InvestmentRiskPage })),
)
const FundingConfigPage = lazy(() =>
  import('@/features/risk-management/components/credit-risk/funding/funding-config-page').then((m) => ({ default: m.FundingConfigPage })),
)
const IrrbbPage = lazy(() =>
  import('@/features/risk-management/components/irrbb/irrbb-page').then((m) => ({ default: m.IrrbbPage })),
)
const AlmPage = lazy(() =>
  import('@/features/risk-management/components/alm/alm-page').then((m) => ({ default: m.AlmPage })),
)
const ReconciliationDashboardPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-dashboard-page').then((m) => ({ default: m.ReconciliationDashboardPage })),
)
const MovementsPage = lazy(() =>
  import('@/features/reconciliation/movements/movements-page').then((m) => ({ default: m.MovementsPage })),
)
const MovementGraphPage = lazy(() =>
  import('@/features/reconciliation/movement-graph/movement-graph-page').then((m) => ({ default: m.MovementGraphPage })),
)
// Workflow Studio: local mock of the AI workflow editor (phase 1). DEV builds only.
const WorkflowStudioPage = lazy(() =>
  import('@/features/reconciliation/workflow-studio/workflow-studio-page').then((module) => ({ default: module.WorkflowStudioPage })),
)
// UC-033.8: real-data flows page wired to /v1/Conciliations.
const ReconciliationFlowsPage = lazy(() =>
  import('@/features/reconciliation/flows/flows-page').then((m) => ({ default: m.ReconciliationFlowsPage })),
)
// UC-033.8 — temporary alias to the legacy mock page so the dev can compare
// real vs mock side-by-side during the PR. Delete this entry (and the mock
// page) before merging.
const ReconciliationFlowsMockPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-flows-page').then((m) => ({ default: m.ReconciliationFlowsPage })),
)
// UC-033.9: real-data matching page wired to /v1/Conciliations.
const MatchingPage = lazy(() =>
  import('@/features/reconciliation/matching/matching-page').then((m) => ({ default: m.MatchingPage })),
)
const ConciliationRequestsPage = lazy(() =>
  import('@/features/reconciliation/conciliation-requests/conciliation-requests-page').then((m) => ({ default: m.ConciliationRequestsPage })),
)
const ReconciliationBatchMatchingPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-batch-matching-page').then((m) => ({ default: m.ReconciliationBatchMatchingPage })),
)
const WorkflowsPage = lazy(() =>
  import('@/features/reconciliation/workflows/workflows-page').then((m) => ({ default: m.WorkflowsPage })),
)
const ConciliationRulesPage = lazy(() =>
  import('@/features/reconciliation/conciliation-rules/conciliation-rules-page').then((m) => ({ default: m.ConciliationRulesPage })),
)
const ConciliationRuleFormPage = lazy(() =>
  import('@/features/reconciliation/conciliation-rules/conciliation-rule-form-page').then((m) => ({ default: m.ConciliationRuleFormPage })),
)
// CU-138: the Workflow-level filter gate — which movements are allowed into a
// workflow at all, evaluated at ingestion before any chain instance is created.
const WorkflowFilterGroupsPage = lazy(() =>
  import('@/features/reconciliation/workflow-filter-groups/workflow-filter-groups-page').then((m) => ({ default: m.WorkflowFilterGroupsPage })),
)
const ConciliationRuleChainsPage = lazy(() =>
  import('@/features/reconciliation/conciliation-rule-chains/conciliation-rule-chains-page').then((m) => ({ default: m.ConciliationRuleChainsPage })),
)
const ConciliationRuleChainDetailPage = lazy(() =>
  import('@/features/reconciliation/conciliation-rule-chains/conciliation-rule-chain-detail-page').then((m) => ({ default: m.ConciliationRuleChainDetailPage })),
)
const ConciliationChainInstancesPage = lazy(() =>
  import('@/features/reconciliation/conciliation-chains/conciliation-chain-instances-page').then((m) => ({ default: m.ConciliationChainInstancesPage })),
)
const ReconciliationConciliationCriteriaPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-conciliation-criteria-page').then((m) => ({ default: m.ReconciliationConciliationCriteriaPage })),
)
const AlertsPage = lazy(() =>
  import('@/features/reconciliation/alerts/alerts-page').then((m) => ({ default: m.AlertsPage })),
)
const ReconciliationPostingPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-posting-page').then((m) => ({ default: m.ReconciliationPostingPage })),
)
const ConciliationsPage = lazy(() =>
  import('@/features/reconciliation/conciliations/conciliations-page').then((m) => ({ default: m.ConciliationsPage })),
)
// UC-099: the conciliation workbook (Conciliated / Not Conciliated) as a screen, one week at a time.
const ConciliationReportingPage = lazy(() =>
  import('@/features/reconciliation/conciliation-reporting/conciliation-reporting-page').then((module) => ({ default: module.ConciliationReportingPage })),
)
// UC-102: the monthly provider fee report (CU-159), one month at a time.
const FeeReportPage = lazy(() =>
  import('@/features/reconciliation/fee-report/fee-report-page').then((module) => ({ default: module.FeeReportPage })),
)
// Legacy MOCK reconciliation pages — shown to tenants NOT on the real module
// (see RECONCILIATION_REAL_TENANT_IDS). Paired with the real pages via
// <ReconTenantView/> at the route level.
const ReconciliationDashboardMockPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-dashboard-page.mock').then((m) => ({ default: m.ReconciliationDashboardMockPage })),
)
const ReconciliationMatchingMockPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-matching-page').then((m) => ({ default: m.ReconciliationMatchingPage })),
)
const ReconciliationRequestsMockPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-requests-page').then((m) => ({ default: m.ReconciliationRequestsPage })),
)
const ReconciliationWorkflowsMockPage = lazy(() =>
  import('@/features/reconciliation/components/reconciliation-workflows-page').then((m) => ({ default: m.ReconciliationWorkflowsPage })),
)
const CashForecastPage = lazy(() =>
  import('@/features/cash-forecast/components/cash-forecast/cash-forecast-page').then((m) => ({ default: m.CashForecastPage })),
)
const ActualVsForecastPage = lazy(() =>
  import('@/features/cash-forecast/components/actual-vs-forecast/actual-vs-forecast-page').then((m) => ({ default: m.ActualVsForecastPage })),
)
const ScenariosPage = lazy(() =>
  import('@/features/cash-forecast/components/scenarios/scenarios-page').then((m) => ({ default: m.ScenariosPage })),
)
const NewScenarioPage = lazy(() =>
  import('@/features/cash-forecast/components/scenarios/new-scenario-page').then((m) => ({ default: m.NewScenarioPage })),
)
const InfraCashflowDailyPage = lazy(() =>
  import('@/features/reporting/components/infra/cashflow-daily-page').then((m) => ({ default: m.CashflowDailyPage })),
)
const InfraInterestPaymentsPage = lazy(() =>
  import('@/features/reporting/components/infra/interest-payments-page').then((m) => ({ default: m.InterestPaymentsPage })),
)
const InfraCashflowForecastPage = lazy(() =>
  import('@/features/reporting/components/infra/cashflow-forecast-page').then((m) => ({ default: m.CashflowForecastPage })),
)
const InfraDividendsPage = lazy(() =>
  import('@/features/reporting/components/infra/dividends-page').then((m) => ({ default: m.DividendsPage })),
)

// ── Parse Templates (Admin Console › Master Data) ────────────
const TemplatesListPage = lazy(() =>
  import('@/features/templates/components/templates-list-page').then((m) => ({ default: m.TemplatesListPage })),
)
const TemplateEditorPage = lazy(() =>
  import('@/features/templates/components/editor/editor-page').then((m) => ({ default: m.TemplateEditorPage })),
)
const UploadTemplatePage = lazy(() =>
  import('@/features/templates/components/upload-template-page').then((m) => ({ default: m.UploadTemplatePage })),
)

// ── SFTP Ingestion (Integrations) ────────────────────────────
const SftpConnectionsPage = lazy(() =>
  import('@/features/sftp-ingestion/components/sftp-connections-page').then((module) => ({ default: module.SftpConnectionsPage })),
)
const SftpRulesPage = lazy(() =>
  import('@/features/sftp-ingestion/components/sftp-rules-page').then((module) => ({ default: module.SftpRulesPage })),
)
const ServiceRulesPage = lazy(() =>
  import('@/features/service-rules/components/service-rules-page').then((module) => ({ default: module.ServiceRulesPage })),
)
const SftpLogsPage = lazy(() =>
  import('@/features/sftp-ingestion/components/sftp-logs-page').then((module) => ({ default: module.SftpLogsPage })),
)

// ── Ingestion Activity Hub (Integrations) ────────────────────
const IngestionActivityPage = lazy(() =>
  import('@/features/ingestion-activity/components/ingestion-activity-page').then((module) => ({
    default: module.IngestionActivityPage,
  })),
)

function RouteLoader() {
  return <div className="p-8 text-sm text-muted-foreground">Loading...</div>
}

function lazyRoute(element: React.ReactNode) {
  return <Suspense fallback={<RouteLoader />}>{element}</Suspense>
}

export const router = createBrowserRouter([
  {
    path: '/callback/auth',
    element: lazyRoute(<AuthCallback />),
  },
  {
    // Alias for legacy app compatibility
    path: '/app/dashboard',
    element: lazyRoute(<AuthCallback />),
  },
  {
    // UC-084.1: where th_saas sends an unusable invitation link
    // (`?outcome=invalid|expired|revoked|consumed`) and where the AuthGuard
    // sends a refused first login. Public on purpose: the person has no session.
    path: '/invitation',
    element: lazyRoute(<InvitationOutcomePage />),
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      // ── Roadmap ──────────────────────────────────────
      { path: 'roadmap', element: lazyRoute(<RoadmapPage />) },

      // ── Status board (UC-048) ───────────────────────
      // Open to every authenticated user (UC-092, reverses the page gate of
      // UC-084.6). The maintainer console inside stays super-admin only
      // (UC-050). Tenant scoping is enforced by the gateway from the JWT.
      { path: 'status-board', element: lazyRoute(<StatusBoardPage />) },

      // ── Home ────────────────────────────────────────
      { index: true, element: lazyRoute(<DashboardPage />) },
      { path: 'dashboard', element: lazyRoute(<DashboardPage />) },

      // ── Integrations ─────────────────────────────────
      { path: 'integrations/services', element: lazyRoute(<ServicesPage />) },
      { path: 'integrations/services/:serviceCode', element: lazyRoute(<ConnectionDetailPage />) },
      { path: 'integrations/services/:serviceCode/:connectionUuid', element: lazyRoute(<ConnectionDetailPage />) },
      { path: 'integrations/status', element: lazyRoute(<IntStatusPage />) },
      { path: 'integrations/webhooks', element: lazyRoute(<WebhooksPage />) },
      { path: 'integrations/email-ingestion', element: lazyRoute(<EmailIngestionPage />) },
      { path: 'integrations/email-ingestion/:mailboxUuid/messages', element: lazyRoute(<MailboxMessagesPage />) },
      { path: 'integrations/api-agent', element: lazyRoute(<ApiAgentPage />) },
      { path: 'integrations/sftp', element: lazyRoute(<SftpConnectionsPage />) },
      { path: 'integrations/sftp/rules', element: lazyRoute(<SftpRulesPage />) },
      { path: 'integrations/sftp/logs', element: lazyRoute(<SftpLogsPage />) },
      { path: 'integrations/service-rules', element: lazyRoute(<ServiceRulesPage />) },
      // Spec D5: the screen moved to Reporting › Ingestion; old bookmarks land there.
      { path: INGESTION_PATHS.legacy.slice(1), element: <LegacyIngestionRedirect /> },

      // ── Data ────────────────────────────────────────
      { path: 'data', element: lazyRoute(<DataHubPage />) },
      { path: 'data/dashboard', element: lazyRoute(<DataHubPage />) },
      { path: 'data/financial-events', element: lazyRoute(<FinancialEventsPage />) },
      { path: 'bank-statements', element: lazyRoute(<BankStatementsPage />) },
      { path: 'transactions', element: lazyRoute(<TransactionsPage />) },
      {
        path: 'bank-accounts',
        element: (
          <PermissionRoute check={{ name: Permissions.BankAccounts, level: PermissionLevel.Read }}>
            <BankAccountsPage />
          </PermissionRoute>
        ),
      },
      { path: 'callback/addBank', element: lazyRoute(<OpenBankingCallback />) },
      { path: 'exchange-rates', element: lazyRoute(<ExchangeRatesPage />) },
      { path: 'invoices', element: lazyRoute(<InvoicesPage />) },
      // Deprecated 2026-10: G/L posting is unused and will be rebuilt (th_saas docs/deprecations/2026-10-gl-posting-and-netsuite.md).
      { path: 'transactions-posting', element: <Navigate to="/transactions" replace /> },
      { path: 'transactions-posting/life-cycle', element: lazyRoute(<GlPostingLifeCyclePage />) },
      // Deprecated 2026-10 (th_saas docs/deprecations/2026-10-tags-and-matching-rules.md): old bookmarks land on
      // the screens of the tagging module that replaced them.
      { path: 'admin/tags', element: <Navigate to="/admin/tagging/tags" replace /> },
      { path: 'admin/matching-rules', element: <Navigate to="/admin/tagging/rules" replace /> },
      {
        path: 'admin/tagging/tags',
        element: (
          <PermissionRoute check={{ name: Permissions.Tags, level: PermissionLevel.Read }}>
            {lazyRoute(<ClassificationTagsPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/tagging/rules',
        element: (
          <PermissionRoute check={{ name: Permissions.Tags, level: PermissionLevel.Read }}>
            {lazyRoute(<ClassificationRulesPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/tagging/conflicts',
        element: (
          <PermissionRoute check={{ name: Permissions.Tags, level: PermissionLevel.Read }}>
            {lazyRoute(<ClassificationConflictsPage />)}
          </PermissionRoute>
        ),
      },
      ...['admin/tagging/rules/new', 'admin/tagging/rules/:ruleId'].map((path) => ({
        path,
        element: (
          <PermissionRoute check={{ name: Permissions.Tags, level: PermissionLevel.CreateEdit }}>
            {lazyRoute(<ClassificationRuleEditorPage />)}
          </PermissionRoute>
        ),
      })),
      {
        // The only counterparty route. Consulting, creating and editing are dialogs over this grid,
        // so none of the three has a path of its own — and none is reachable by link.
        path: 'admin/counterparties',
        element: (
          <PermissionRoute
            check={{ name: Permissions.Counterparties, level: PermissionLevel.Read }}
          >
            {lazyRoute(<CounterpartiesPage />)}
          </PermissionRoute>
        ),
      },
      {
        // No granular permission exists in core for the approval rules, and the write endpoint is admin-only
        // ([UserIsAuthorized]), so the guard mirrors it instead of inventing a permission string.
        path: 'admin/payment-approvals',
        element: (
          <PermissionRoute requireAdmin>
            {lazyRoute(<PaymentApprovalRulesPage />)}
          </PermissionRoute>
        ),
      },

      // ── Parse Templates ─────────────────────────────
      { path: 'parse-templates', element: lazyRoute(<TemplatesListPage />) },
      { path: 'parse-templates/new', element: lazyRoute(<TemplateEditorPage />) },
      { path: 'parse-templates/upload', element: lazyRoute(<UploadTemplatePage />) },
      { path: 'parse-templates/:templateId', element: lazyRoute(<TemplateEditorPage />) },
      { path: 'data/exports', element: lazyRoute(<ReportsPage />) },
      { path: 'data/fx-contracts', element: lazyRoute(<FxContractsPage />) },
      { path: 'data/loans', element: lazyRoute(<DebtsContractsPage />) },
      { path: 'data/investments', element: lazyRoute(<InvestmentsContractsPage />) },

      { path: 'data/repository', element: lazyRoute(<DataRepositoryPage />) },
      { path: 'data/exports', element: lazyRoute(<DataExportsPage />) },
      { path: 'data/journal-entries', element: <DemoPage title="Journal Entries" /> },
      {
        path: 'data/custom-entities',
        element: (
          <PermissionRoute requireAdmin>
            {lazyRoute(<CustomEntityBuilderPage />)}
          </PermissionRoute>
        ),
      },

      // ── Workflows ─────────────────────────────────────
      { path: 'workflows/dashboard', element: lazyRoute(<WorkflowsDashboardPage />) },
      { path: 'workflows/builder', element: lazyRoute(<WorkflowBuilderPage />) },
      { path: 'workflows/approvals', element: lazyRoute(<WorkflowApprovalsPage />) },

      // ── Reconciliation ──────────────────────────────
      // UC-033: real Conciliations grid wired to Reconciliation.Api via gateway.
      // FE access is intentionally not gated by Reconciliation.Conciliations
      // while module-level gating (EnabledModules/PageStatuses) is pending.
      // Backend still enforces via [UserPermission] — see UC-033 §Business Rules.
      { path: 'reconciliation/conciliations', element: lazyRoute(<ConciliationsPage />) },
      { path: 'reconciliation/conciliation-reporting', element: lazyRoute(<ConciliationReportingPage />) },
      { path: 'reconciliation/fee-report', element: lazyRoute(<FeeReportPage />) },
      { path: 'reconciliation/dashboard', element: lazyRoute(<ReconTenantView real={<ReconciliationDashboardPage />} mock={<ReconciliationDashboardMockPage />} />) },
      { path: 'reconciliation/status', element: <DemoPage title="Reconciliation Status" /> },
      { path: 'reconciliation/matching-1to1', element: lazyRoute(<ReconTenantView real={<MatchingPage />} mock={<ReconciliationMatchingMockPage />} />) },
      { path: 'reconciliation/batch-matching', element: lazyRoute(<ReconciliationBatchMatchingPage />) },
      { path: 'reconciliation/approvals', element: <DemoPage title="Reconciliation Approvals" /> },
      { path: 'reconciliation/workflows', element: lazyRoute(<ReconTenantView real={<WorkflowsPage />} mock={<ReconciliationWorkflowsMockPage />} />) },
      // Workflow Studio (UC-077): local development, and builds whose env file sets
      // VITE_WORKFLOW_STUDIO=true (QA).
      ...(isWorkflowStudioEnabled()
        ? [
            { path: 'reconciliation/workflows/studio/new', element: lazyRoute(<WorkflowStudioPage />) },
            { path: 'reconciliation/workflows/:wfId/studio', element: lazyRoute(<WorkflowStudioPage />) },
          ]
        : []),
      // UC-033.7: drill-down CRUD for ConciliationRule. Rules keep these FLAT
      // routes (UC-033.15 D1) — the chain-nested variant broke editing (PR #41).
      { path: 'reconciliation/workflows/:wfId/rules', element: lazyRoute(<ConciliationRulesPage />) },
      { path: 'reconciliation/workflows/:wfId/filter-groups', element: lazyRoute(<WorkflowFilterGroupsPage />) },
      { path: 'reconciliation/workflows/:wfId/rules/new', element: lazyRoute(<ConciliationRuleFormPage />) },
      { path: 'reconciliation/workflows/:wfId/rules/:ruleId/edit', element: lazyRoute(<ConciliationRuleFormPage />) },
      // UC-033.15: sibling drill-down ABM for ConciliationRuleChain (CU-105).
      { path: 'reconciliation/workflows/:wfId/chains', element: lazyRoute(<ConciliationRuleChainsPage />) },
      { path: 'reconciliation/workflows/:wfId/chains/:chainId', element: lazyRoute(<ConciliationRuleChainDetailPage />) },
      // CU-110/CU-111: chain execution instances — flows-style list with rows
      // that expand to the projected tree (topology + runtime status, OR branches).
      { path: 'reconciliation/workflows/:wfId/chain-instances', element: lazyRoute(<ConciliationChainInstancesPage />) },
      // Global entry (Reconciliation Status menu): no wfId — the page defaults to
      // the first workflow and exposes an in-page Workflow filter.
      { path: 'reconciliation/chain-instances', element: lazyRoute(<ConciliationChainInstancesPage />) },
      { path: 'reconciliation/conciliation-criteria', element: lazyRoute(<ReconciliationConciliationCriteriaPage />) },
      { path: 'reconciliation/posting', element: lazyRoute(<ReconciliationPostingPage />) },
      { path: 'reconciliation/movements', element: lazyRoute(<MovementsPage />) },
      { path: 'reconciliation/movement-graph', element: lazyRoute(<MovementGraphPage />) },
      { path: 'reconciliation/flows', element: lazyRoute(<ReconTenantView real={<ReconciliationFlowsPage />} mock={<ReconciliationFlowsMockPage />} />) },
      { path: 'reconciliation/requests', element: lazyRoute(<ReconTenantView real={<ConciliationRequestsPage />} mock={<ReconciliationRequestsMockPage />} />) },

      // ── Payments ────────────────────────────────────
      { path: 'payments/approval-rules', element: <DemoPage title="Payments Approval Rules" /> },
      { path: 'payments', element: lazyRoute(<PaymentsPage />) },
      { path: 'payments/:id/details', element: lazyRoute(<PaymentDetailPage />) },
      { path: 'payments/approval', element: lazyRoute(<PaymentApprovalPage />) },
      { path: 'payments/life-cycle-blotter', element: lazyRoute(<PaymentLifeCycleBlotterPage />) },
      { path: 'payments/net-settlements', element: lazyRoute(<NetSettlementsPage />) },

      // ── Reporting ───────────────────────────────────
      { path: 'reporting/dashboard', element: lazyRoute(<ReportingDashboardPage />) },
      { path: 'reporting/builder', element: lazyRoute(<ReportBuilderPage />) },
      { path: 'reporting/alerts', element: lazyRoute(<ReportingAlertsPage />) },
      { path: 'reporting/audit-trail', element: lazyRoute(<ReportingAuditTrailPage />) },
      { path: 'reporting/datalake/cash-position', element: lazyRoute(<DatalakeCashPositionPage />) },
      { path: INGESTION_PATHS.page.slice(1), element: lazyRoute(<IngestionActivityPage />) },
      { path: 'reporting/datalake/balance-change', element: lazyRoute(<DatalakeBalanceChangePage />) },
      { path: 'reporting/datalake/account-statement', element: lazyRoute(<DatalakeAccountStatementPage />) },
      { path: 'reporting/datalake/cash-flow', element: lazyRoute(<DatalakeCashFlowPage />) },
      { path: 'reporting/datalake/report-builder', element: <Navigate to="/reporting/reports/new" replace /> },
      { path: 'reporting/reports', element: <ReportsRoute minLevel={PermissionLevel.Read}>{lazyRoute(<DatalakeReportsListPage />)}</ReportsRoute> },
      { path: 'reporting/reports/new', element: <ReportsRoute minLevel={PermissionLevel.Read}>{lazyRoute(<DatalakeReportEditorPage />)}</ReportsRoute> },
      { path: 'reporting/reports/:reportId', element: <ReportsRoute minLevel={PermissionLevel.Read}>{lazyRoute(<DatalakeReportEditorPage />)}</ReportsRoute> },
      { path: 'reporting/cash-position', element: lazyRoute(<CashPositionPage />) },
      { path: 'reporting/cash-position/overview', element: lazyRoute(<CashOverviewPage />) },
      // Cash & Liquidity
      { path: 'reporting/cash-liquidity/cash-forecast', element: lazyRoute(<CashForecastPage />) },
      { path: 'reporting/cash-liquidity/actual-vs-forecast', element: lazyRoute(<ActualVsForecastPage />) },
      { path: 'reporting/cash-liquidity/scenarios', element: lazyRoute(<ScenariosPage />) },
      { path: 'reporting/cash-liquidity/scenarios/new', element: lazyRoute(<NewScenarioPage />) },
      {
        path: 'reporting/cash-liquidity/balance-difference',
        element: (
          <PermissionRoute check={{ name: Permissions.CashPosition, level: PermissionLevel.Read }}>
            {lazyRoute(<BalanceDifferencePage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'reporting/cash-liquidity/category-positions',
        element: (
          <PermissionRoute check={{ name: Permissions.CashPosition, level: PermissionLevel.Read }}>
            {lazyRoute(<CategoryPositionsPage />)}
          </PermissionRoute>
        ),
      },
      // Cash & Liquidity — Infra tenant custom reports (TenantId 41)
      { path: 'reporting/cash-liquidity/cashflow-daily',    element: lazyRoute(<InfraCashflowDailyPage />) },
      { path: 'reporting/cash-liquidity/interest-payments', element: lazyRoute(<InfraInterestPaymentsPage />) },
      { path: 'reporting/cash-liquidity/cashflow-forecast', element: lazyRoute(<InfraCashflowForecastPage />) },
      { path: 'reporting/cash-liquidity/dividends',         element: lazyRoute(<InfraDividendsPage />) },
      // Kept routes (removed from nav)
      { path: 'reporting/cash-liquidity/cash-position', element: <DemoPage title="Cash Position + Forecast" /> },
      { path: 'reporting/cash-liquidity/debt-repayment', element: <DemoPage title="Debt Repayment Schedule" /> },
      // Operations
      { path: 'reporting/operations/fx-contracts', element: <Suspense><FxBlotterPage /></Suspense> },
      { path: 'reporting/operations/loans', element: <Suspense><LoansBlotterPage /></Suspense> },
      { path: 'reporting/operations/investments', element: <Suspense><InvestmentsBlotterPage /></Suspense> },
      { path: 'reporting/operations/investments-movements', element: lazyRoute(<InvestmentsMovementsPage />) },
      { path: 'reporting/operations/recon-status', element: <DemoPage title="Reconciliation Status" /> },
      { path: 'reporting/operations/debt-repayment', element: <Suspense><DebtRepaymentPage /></Suspense> },
      { path: 'reporting/operations/customer-aging', element: <Suspense><CustomerAgingPage /></Suspense> },
      // Kept routes (removed from nav)
      { path: 'reporting/operations/transactions', element: <DemoPage title="Transactions" /> },
      { path: 'reporting/operations/bank-accounts', element: <DemoPage title="Bank Accounts" /> },
      { path: 'reporting/operations/failed-payments', element: <DemoPage title="Failed Payments" /> },
      { path: 'reporting/operations/pending-approvals', element: <DemoPage title="Pending Approvals" /> },
      // Risk Management
      { path: 'reporting/risk/cockpit', element: lazyRoute(<RiskCockpitPage />) },
      { path: 'reporting/risk/mtm', element: lazyRoute(<MtmPage />) },
      { path: 'reporting/risk/fx-exposure', element: lazyRoute(<FxExposurePage />) },
      { path: 'reporting/risk/investments', element: lazyRoute(<InvestmentRiskPage />) },
      { path: 'reporting/risk/credit-risk', element: lazyRoute(<CreditRiskPage />) },
      { path: 'reporting/risk/credit-risk/funding/configure', element: lazyRoute(<FundingConfigPage />) },
      { path: 'reporting/risk/irrbb', element: lazyRoute(<IrrbbPage />) },
      { path: 'reporting/risk/alm', element: lazyRoute(<AlmPage />) },
      { path: 'reporting/risk/var', element: <DemoPage title="VaR" /> },
      { path: 'reporting/risk/stress-test', element: <DemoPage title="Stress Test" /> },
      { path: 'reporting/risk/concentration', element: <DemoPage title="Concentration" /> },
      { path: 'reporting/risk/debt-covenants', element: <DemoPage title="Debt Covenants" /> },
      // Regulatory & Compliance
      { path: 'reporting/regulatory/car', element: <DemoPage title="Monthly Return on CAR" /> },
      { path: 'reporting/regulatory/icaap', element: <DemoPage title="ICAAP" /> },
      { path: 'reporting/regulatory/cmp', element: <DemoPage title="Capital Management Plan (CMP)" /> },
      { path: 'reporting/regulatory/lcr', element: <DemoPage title="Liquidity Coverage Ratio (LCR)" /> },
      { path: 'reporting/regulatory/nsfr', element: <DemoPage title="Net Stable Funding Ratio (NSFR)" /> },
      { path: 'reporting/regulatory/crr', element: <DemoPage title="Cash Reserve Ratio (CRR)" /> },
      { path: 'reporting/regulatory/assets-liabilities', element: <DemoPage title="Return on Assets and Liabilities" /> },
      { path: 'reporting/regulatory/large-exposures', element: <DemoPage title="Return on Large Exposures" /> },
      { path: 'reporting/regulatory/npl', element: <DemoPage title="NPLs and Provisions" /> },

      // ── Alerts ────────────────────────────────────────
      { path: 'alerts/dashboard', element: lazyRoute(<AlertsDashboardPage />) },
      { path: 'alerts/integrations', element: lazyRoute(<AlertsIntegrationsPage />) },
      { path: 'alerts/data', element: lazyRoute(<AlertsDataPage />) },
      { path: 'alerts/reconciliation', element: lazyRoute(<AlertsPage />) },
      { path: 'alerts/gl-posting', element: lazyRoute(<GlPostingAlertsPage />) },
      { path: 'alerts/payments', element: lazyRoute(<AlertsPaymentsPage />) },
      { path: 'alerts/reporting', element: lazyRoute(<AlertsReportingPage />) },
      { path: 'alerts/prefunding', element: lazyRoute(<PrefundingAlertsPage />) },
      { path: 'alerts/workflows', element: lazyRoute(<AlertsWorkflowsPage />) },
      { path: 'alerts/agents', element: lazyRoute(<AlertsAgentsPage />) },
      { path: 'alerts/admin', element: lazyRoute(<AlertsAdminPage />) },

      // ── Agents ─────────────────────────────────────────
      { path: 'agents/dashboard', element: lazyRoute(<AgentsDashboardPage />) },
      { path: 'agents/builder', element: lazyRoute(<AgentBuilderPage />) },
      { path: 'agents/approvals', element: lazyRoute(<AgentApprovalsPage />) },
      { path: 'agents/chats', element: lazyRoute(<AgentChatsPage />) },
      { path: 'agents/logs', element: lazyRoute(<AgentLogsPage />) },

      // ── Admin Console ───────────────────────────────
      { path: 'admin/user-agent-mgm', element: lazyRoute(<AdminConsolePage />) },
      {
        path: 'admin/ai-usage',
        element: (
          <PermissionRoute requireAdmin>
            {lazyRoute(<AiUsagePage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <PermissionRoute check={{ name: Permissions.UserManagement, level: PermissionLevel.Read }}>
            {lazyRoute(<UsersPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/roles',
        element: (
          <PermissionRoute check={{ name: Permissions.UserManagement, level: PermissionLevel.Read }}>
            {lazyRoute(<RolesPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/audit',
        element: (
          <PermissionRoute check={{ name: Permissions.UserManagement, level: PermissionLevel.Read }}>
            {lazyRoute(<AuditTrailPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/groups',
        element: (
          <PermissionRoute check={{ name: Permissions.UserGroups, level: PermissionLevel.Admin }}>
            {lazyRoute(<GroupsPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/access-tokens',
        element: (
          <PermissionRoute check={{ name: Permissions.AccessTokens, level: PermissionLevel.Admin }}>
            {lazyRoute(<AccessTokensPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/companies',
        element: (
          <PermissionRoute check={{ name: Permissions.Companies, level: PermissionLevel.Read }}>
            {lazyRoute(<CompaniesPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/company-groups',
        element: (
          <PermissionRoute check={{ name: Permissions.CompanyGroups, level: PermissionLevel.Read }}>
            {lazyRoute(<CompanyGroupsPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: 'admin/static-data',
        element: (
          <PermissionRoute requireAdmin>
            {lazyRoute(<StaticDataPage />)}
          </PermissionRoute>
        ),
      },

      // ── Deprecated (not in nav, accessible by URL) ──
      { path: 'admin/api-access', element: lazyRoute(<ApiAccessPage />) },
      { path: 'transactions/hierarchical', element: lazyRoute(<TransactionsHierarchicalPage />) },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
])
